import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import {
  FileText, Plus, Download, Loader2, Eye, Trash2, Send, Briefcase
} from "lucide-react";

export function ProposalGenerator({ open, onOpenChange, clients = [] }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ client_id: "", discovery_notes: "", service_type: "Strategic Foundation Retainer", custom_price: "" });

  useEffect(() => { if (open) fetchProposals(); }, [open]);

  const fetchProposals = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/proposals`);
      if (res.ok) setProposals((await res.json()).proposals || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleGenerate = async () => {
    if (!form.client_id) { toast.error("Select a client"); return; }
    setGenerating(true);
    try {
      const res = await authFetch(`${API}/consultant/generate-proposal`, { method: "POST", body: JSON.stringify(form) });
      if (res.ok) { toast.success("Proposal generated!"); setShowCreate(false); fetchProposals(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed"); }
    } catch { toast.error("Generation failed"); }
    setGenerating(false);
  };

  const downloadPDF = (proposalId) => {
    window.open(`${API}/consultant/proposals/${proposalId}/pdf`, '_blank');
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="proposal-generator">
        <DialogHeader className="p-5 border-b">
          <DialogTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#2BC40E]" /> Proposal Generator</DialogTitle>
          <DialogDescription>AI-powered branded consulting proposals</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" /></div> : (
            <>
              <div className="flex justify-between mb-4">
                <h3 className="font-semibold text-neutral-900">Proposals ({proposals.length})</h3>
                <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#333333] text-white" data-testid="create-proposal-btn">
                  <Plus className="w-4 h-4 mr-1" /> New Proposal
                </Button>
              </div>
              {proposals.length === 0 ? <p className="text-neutral-500 text-center py-12">No proposals yet</p> : (
                <div className="space-y-3">
                  {proposals.map(p => (
                    <Card key={p.proposal_id} className="border-neutral-200">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-neutral-900">{p.proposal?.proposal_title || p.service_type}</p>
                          <p className="text-xs text-neutral-500">{p.client_name} — {p.company}</p>
                          <p className="text-xs text-neutral-400">{p.created_at?.slice(0, 10)}</p>
                        </div>
                        <Badge className={p.status === "draft" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}>{p.status}</Badge>
                        <Button size="sm" variant="outline" onClick={() => downloadPDF(p.proposal_id)} data-testid={`download-${p.proposal_id}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> PDF
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Generate New Proposal</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs">Client</Label>
                <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name} {c.company ? `(${c.company})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Service Type</Label>
                <Select value={form.service_type} onValueChange={v => setForm({ ...form, service_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Strategic Foundation Retainer">Strategic Foundation ($750/mo)</SelectItem>
                    <SelectItem value="Growth & Optimization Retainer">Growth & Optimization ($2,500/mo)</SelectItem>
                    <SelectItem value="Scale & Leadership Retainer">Scale & Leadership ($5,000/mo)</SelectItem>
                    <SelectItem value="R.A.P.I.D. Program">R.A.P.I.D. Program ($125)</SelectItem>
                    <SelectItem value="Project-Based Consulting">Project-Based ($600/mo)</SelectItem>
                    <SelectItem value="Custom">Custom Engagement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.service_type === "Custom" && (
                <div><Label className="text-xs">Custom Price</Label><Input value={form.custom_price} onChange={e => setForm({ ...form, custom_price: e.target.value })} placeholder="$3,000/mo for 6 months" /></div>
              )}
              <div>
                <Label className="text-xs">Discovery Notes</Label>
                <Textarea value={form.discovery_notes} onChange={e => setForm({ ...form, discovery_notes: e.target.value })} placeholder="Key insights from discovery call, specific pain points, goals discussed..." className="min-h-24" />
              </div>
              <Button onClick={handleGenerate} disabled={generating} className="w-full bg-[#333333] text-white">
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                {generating ? "Generating..." : "Generate Proposal"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default ProposalGenerator;
